import {notEmpty} from '@wandb/weave/common/util/obj';
import {pick} from 'lodash';
import {useMemo} from 'react';

import {
  RunSelectorCsvExportRunsQueryVariables,
  useRunSelectorCsvExportRunsQuery,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import * as Filters from '../../../util/filters';
import * as Query from '../../../util/queryts';
import * as Run from '../../../util/runs';

const EXPORTED_RUNS_LIMIT = 10000;

export const useCsvExportRuns = (topLevelQueryVariables: Query.Query) => {
  const queryVariables = useMemo(() => {
    // these are vars we'll pass to the query as-is
    const passThroughVars = pick(topLevelQueryVariables, [
      'aggregationKeys',
      'configKeys',
      'enableArtifactCounts',
      'enableTags',
      'entityName',
      'projectName',
      'summaryKeys',
      'wandbKeys',
    ]);

    // some things we need to process more before the query.
    // the shape of topLevelQueryVariables will change once we're
    // fully off of runs-low across RunSelector, at which point
    // this extra processing will be centralized elsewhere.
    const {
      aggregationKeys,
      configKeys,
      filters,
      grouping,
      selections,
      sort,
      summaryKeys,
    } = topLevelQueryVariables;
    const enableAggregations = !!aggregationKeys?.length;
    const enableConfig = !!configKeys?.length;
    const enableSummary = !!summaryKeys?.length;
    const filtersWithSelections =
      selections != null ? Filters.And([filters, selections]) : filters;
    const groupKeys = grouping?.map(Run.keyToServerPath) ?? [];
    const groupLevel = 0;
    const order = Query.sortToOrderString(sort);

    // these the variables that are actually used
    const variables: RunSelectorCsvExportRunsQueryVariables = {
      ...passThroughVars,
      enableAggregations,
      enableConfig,
      enableSummary,
      filters: JSON.stringify(Filters.toMongo(filtersWithSelections)),
      groupKeys,
      groupLevel,
      limit: EXPORTED_RUNS_LIMIT,
      order,
    };
    return variables;
  }, [topLevelQueryVariables]);

  const {loading, data} = useRunSelectorCsvExportRunsQuery({
    context: propagateErrorsContext(),
    variables: queryVariables,
    fetchPolicy: 'no-cache',
  });

  const runs = useMemo(
    () =>
      data?.project?.runs?.edges
        ?.map(({node}) => Run.fromJson(node))
        .filter(notEmpty) ?? [],
    [data?.project?.runs?.edges]
  );

  return {
    isLoading: loading,
    runs,
  };
};
